import { useState, useEffect } from "react";
import { SITE_CONFIG } from "../../../app/config";
import { BlogLayout } from "../BlogLayout";

const StreamingCalculator = (): JSX.Element => {
  const [monthlyPrice, setMonthlyPrice] = useState(11.99);
  const [years, setYears] = useState(30);
  const [annualIncrease, setAnnualIncrease] = useState(5);
  const [shareStatus, setShareStatus] = useState<"idle" | "copied" | "shared">("idle");
  const [isFromShare, setIsFromShare] = useState(false);

  // Load values from URL params (when someone opens a shared link)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const priceParam = params.get("price");
    const yearsParam = params.get("years");
    const increaseParam = params.get("increase");

    if (priceParam || yearsParam || increaseParam) {
      setIsFromShare(true);
      if (priceParam) setMonthlyPrice(parseFloat(priceParam) || 11.99);
      if (yearsParam) setYears(parseInt(yearsParam) || 30);
      if (increaseParam) setAnnualIncrease(parseFloat(increaseParam) || 5);
    }
  }, []);

  // Calculate total streaming cost with compound increases
  const calculateStreamingCost = () => {
    let total = 0;
    let yearlyPrice = monthlyPrice * 12;
    for (let i = 0; i < years; i++) {
      total += yearlyPrice;
      yearlyPrice *= (1 + annualIncrease / 100);
    }
    return Math.round(total);
  };

  const streamingTotal = calculateStreamingCost();
  const avgCdPrice = 12;
  const avgVinylPrice = 28;
  const cdsYouCouldBuy = Math.floor(streamingTotal / avgCdPrice);
  const vinylsYouCouldBuy = Math.floor(streamingTotal / avgVinylPrice);
  const mixedCollection = Math.floor(streamingTotal / ((avgCdPrice + avgVinylPrice) / 2)); // 50/50 mix

  const generateShareText = () => {
    return `🎵 I calculated my streaming costs and I'm SHOOK!

💸 ${years} years of streaming at $${monthlyPrice}/month = $${streamingTotal.toLocaleString()}
📀 That's ${mixedCollection.toLocaleString()} albums I could OWN instead!

Stop renting your music. Start owning it. 

Calculate yours: ${window.location.origin}/blog/vinyl-cd-vs-streaming-true-cost

#GoOffline #VinylCollection #OwnYourMusic`;
  };

  const generateShareUrl = () => {
    const params = new URLSearchParams({
      price: monthlyPrice.toString(),
      years: years.toString(),
      increase: annualIncrease.toString()
    });
    return `${window.location.origin}/blog/vinyl-cd-vs-streaming-true-cost?${params.toString()}`;
  };

  const handleShare = async () => {
    const shareText = generateShareText();
    const shareUrl = generateShareUrl();

    // Try Web Share API first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Streaming Cost Calculator Results",
          text: shareText,
          url: shareUrl
        });
        setShareStatus("shared");
        setTimeout(() => setShareStatus("idle"), 3000);
        return;
      } catch {
        // User cancelled or share failed, fall through to clipboard
      }
    }

    // Fallback: copy to clipboard
    try {
      await navigator.clipboard.writeText(shareText + "\n\n" + shareUrl);
      setShareStatus("copied");
      setTimeout(() => setShareStatus("idle"), 3000);
    } catch {
      // Final fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareText + "\n\n" + shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShareStatus("copied");
      setTimeout(() => setShareStatus("idle"), 3000);
    }
  };

  return (
    <div className="calculator-wrapper">
      {isFromShare && (
        <div className="calc-shared-banner">
          <span className="shared-banner-icon">🔗</span>
          <span className="shared-banner-text">Someone shared their streaming cost calculation with you!</span>
        </div>
      )}
      <div className="calculator-header">
        <span className="calculator-icon">🧮</span>
        <h3>Streaming vs. Ownership Calculator</h3>
      </div>
      
      <div className="calculator-inputs">
        <div className="calc-input-group">
          <label htmlFor="monthlyPrice">Monthly streaming cost ($)</label>
          <input
            type="number"
            id="monthlyPrice"
            value={monthlyPrice}
            onChange={(e) => setMonthlyPrice(Math.max(0, parseFloat(e.target.value) || 0))}
            min="0"
            step="0.01"
          />
        </div>
        
        <div className="calc-input-group">
          <label htmlFor="years">Years you'll subscribe</label>
          <input
            type="number"
            id="years"
            value={years}
            onChange={(e) => setYears(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
            min="1"
            max="50"
          />
        </div>
        
        <div className="calc-input-group">
          <label htmlFor="increase">Annual price increase (%)</label>
          <input
            type="number"
            id="increase"
            value={annualIncrease}
            onChange={(e) => setAnnualIncrease(Math.max(0, Math.min(20, parseFloat(e.target.value) || 0)))}
            min="0"
            max="20"
          />
        </div>
      </div>

      <div className="calculator-results">
        <div className="calc-result streaming-result">
          <span className="result-emoji">💸</span>
          <div className="result-content">
            <span className="result-label">Total streaming cost over {years} years</span>
            <span className="result-value">${streamingTotal.toLocaleString()}</span>
            <span className="result-note">And you'll own: <strong>nothing</strong></span>
          </div>
        </div>

        <div className="calc-divider">
          <span>OR WITH THAT MONEY...</span>
        </div>

        <div className="calc-alternatives">
          <div className="calc-alt-item cd">
            <span className="alt-emoji">💿</span>
            <div className="alt-content">
              <span className="alt-value">{cdsYouCouldBuy.toLocaleString()} CDs</span>
              <span className="alt-note">@ ${avgCdPrice} average</span>
            </div>
          </div>
          
          <div className="calc-alt-item vinyl">
            <span className="alt-emoji">🎵</span>
            <div className="alt-content">
              <span className="alt-value">{vinylsYouCouldBuy.toLocaleString()} Vinyls</span>
              <span className="alt-note">@ ${avgVinylPrice} average</span>
            </div>
          </div>
          
          <div className="calc-alt-item mixed">
            <span className="alt-emoji">📀</span>
            <div className="alt-content">
              <span className="alt-value">{mixedCollection.toLocaleString()} Albums</span>
              <span className="alt-note">Mixed CD + Vinyl collection</span>
            </div>
          </div>
        </div>

        <div className="calc-legacy">
          <span className="legacy-emoji">👴</span>
          <p>
            In {years} years, you could show your grandchildren <strong>{mixedCollection.toLocaleString()} albums</strong> that 
            shaped your life — tangible memories they can hold, play, and inherit. 
            Or you could show them... a cancelled subscription.
          </p>
        </div>

        <div className="calc-share-section">
          <button 
            className={`calc-share-btn ${shareStatus !== "idle" ? "success" : ""}`}
            onClick={handleShare}
          >
            <span className="share-icon">
              {shareStatus === "idle" && "📤"}
              {shareStatus === "copied" && "✅"}
              {shareStatus === "shared" && "🚀"}
            </span>
            <span className="share-text">
              {shareStatus === "idle" && "Share Your Results"}
              {shareStatus === "copied" && "Copied to Clipboard!"}
              {shareStatus === "shared" && "Shared!"}
            </span>
          </button>
          <p className="calc-share-hint">Show your friends how much they're wasting on streaming!</p>
        </div>
      </div>
    </div>
  );
};

export const VinylCdVsStreaming = (): JSX.Element => (
  <BlogLayout
    title="Vinyl & CD vs. Streaming: The True Cost of Music Ownership in 2025"
    subtitle="We crunch the numbers on what you're really paying for your music — and why physical might be the smarter long-term play."
    description="Vinyl & CD vs streaming: we break down the true cost of music ownership in 2025. Is Spotify actually cheaper than owning physical music? The math may surprise you."
    date="September 15, 2024"
    dateISO="2024-09-15"
    path="/blog/vinyl-cd-vs-streaming-true-cost"
    readTime="12 min"
  >
    <section>
      <p className="blog-intro">
        Let's be real: streaming is convenient. You tap a button, and suddenly you've got access to 100 million songs. 
        But here's the thing nobody talks about — <strong>you don't own any of it</strong>. You're renting. And that rent 
        keeps going up.
      </p>
      <p>
        In this deep dive, we're breaking down the true cost of streaming versus building a physical collection. 
        We'll look at the numbers, the hidden gotchas, and why savvy music lovers are going back to vinyl and CD. 
        Plus, we'll show you how to have the best of both worlds by ripping your collection for personal streaming.
      </p>
    </section>

    <section>
      <h2>The Streaming Price Creep Is Real</h2>
      <p>
        Remember when Spotify was $9.99/month? Those days are gone. Here's a quick history of streaming price hikes:
      </p>
      <ul>
        <li><strong>2023:</strong> Spotify raised US prices to $10.99/month (Premium) and $16.99/month (Family)</li>
        <li><strong>2024:</strong> Another bump — Premium hit $11.99, Family went to $19.99</li>
        <li><strong>Apple Music:</strong> Increased from $9.99 to $10.99 (individual) in late 2022</li>
        <li><strong>YouTube Music:</strong> Premium jumped from $9.99 to $13.99 in 2023</li>
      </ul>
      <p>
        The pattern is clear: <strong>streaming prices only go up</strong>. And they'll keep climbing as these companies 
        chase profitability. Your monthly fee today won't be your monthly fee in 5 years — or 30 years.
      </p>
    </section>

    <section>
      <h2>Calculate Your True Streaming Cost</h2>
      <p>
        Use this calculator to see what streaming will really cost you over your lifetime — and what you could 
        own instead:
      </p>
      <StreamingCalculator />
    </section>

    <section>
      <h2>The 30-Year Reality Check</h2>
      <p>
        Let's think long-term. If you're 25 today and plan to listen to music until you're 55 (spoiler: you will), 
        here's what 30 years of streaming looks like:
      </p>
      
      <h3>Streaming (Spotify Premium) — 30 Years</h3>
      <div className="blog-calculation">
        <p>Starting price: $11.99/month</p>
        <p>Assuming 5% annual increase (historically accurate):</p>
        <ul>
          <li>Years 1-5: ~$144 → $184/year = <strong>$820</strong></li>
          <li>Years 6-10: ~$193 → $247/year = <strong>$1,100</strong></li>
          <li>Years 11-15: ~$259 → $331/year = <strong>$1,475</strong></li>
          <li>Years 16-20: ~$347 → $443/year = <strong>$1,975</strong></li>
          <li>Years 21-25: ~$465 → $594/year = <strong>$2,645</strong></li>
          <li>Years 26-30: ~$623 → $795/year = <strong>$3,545</strong></li>
        </ul>
        <p className="blog-total"><strong>30-Year Total: ~$11,560</strong></p>
        <p className="blog-note">After 30 years? You own: <strong>Absolutely nothing.</strong></p>
        <p className="blog-note">At 5% annual increase, you'll be paying <strong>$66/month</strong> by year 30.</p>
      </div>

      <h3>Physical Collection — 30 Years</h3>
      <div className="blog-calculation">
        <p>Average CD price: $8-15 (used/new on <a href="https://www.cdandlp.com/?affilie=columbo&utm_source=gooffline.me/blog&utm_medium=link&utm_campaign=affiliation" target="_blank" rel="noopener noreferrer sponsored">CD&LP</a>)</p>
        <p>Average Vinyl price: $20-35</p>
        <p>Buying 10 albums/year for 30 years = 300 albums:</p>
        <ul>
          <li>150 CDs at $12 average = $1,800</li>
          <li>150 Vinyls at $28 average = $4,200</li>
        </ul>
        <p className="blog-total"><strong>30-Year Total: ~$6,000</strong></p>
        <p className="blog-note">After 30 years? You own: <strong>300 albums forever.</strong></p>
        <p className="blog-note">Estimated collection value (with appreciation): <strong>$8,000-15,000+</strong></p>
      </div>

      <p>
        <strong>The math is brutal:</strong> Streaming costs almost <em>double</em> what a substantial physical 
        collection costs over 30 years. And while your streaming subscription becomes worthless the moment you 
        stop paying, your vinyl and CDs are assets you own forever.
      </p>
    </section>

    <section>
      <h2>The Legacy Factor: Music You Can Pass Down</h2>
      <p>
        Here's something the streaming companies will never talk about: <strong>legacy</strong>.
      </p>
      <p>
        Imagine this: You're 65. Your grandkids come over. You walk them to your living room where 
        300 albums sit on the shelf — decades of your life in physical form. You pull out your 
        worn copy of "The Dark Side of the Moon" and say, "This changed my life when I was your age."
      </p>
      <p>
        You drop the needle. The room fills with music. They watch the record spin. They hold 
        the cover art. They read the liner notes. It's <em>real</em> — tangible history they can 
        touch, and one day inherit.
      </p>
      <p>
        Now imagine the streaming alternative: "Hey kids, want to see my music collection?" 
        You open an app. "I used to have 5,000 songs in my library, but they removed half of them 
        over the years, and also I had to cancel when the price hit $80/month..."
      </p>
      <p>
        <strong>Physical music is legacy.</strong> It's a time capsule of your life that outlives 
        any streaming service, any app, any corporation. Your grandchildren can inherit your records. 
        They can't inherit your Spotify login.
      </p>
    </section>

    <section>
      <h2>The Disappearing Music Problem</h2>
      <p>
        Here's something streaming companies don't advertise: <strong>music disappears</strong>. Licensing deals expire, 
        artists pull their catalogs, and suddenly that album you've been listening to for years... is just gone.
      </p>
      <p>Some high-profile examples:</p>
      <ul>
        <li><strong>Taylor Swift's original recordings</strong> — pulled from Spotify during her re-recording campaign</li>
        <li><strong>Neil Young</strong> — removed his entire catalog over Spotify policy disputes</li>
        <li><strong>Joni Mitchell</strong> — followed Neil Young out the door</li>
        <li><strong>King Crimson</strong> — notoriously kept music off streaming for years</li>
        <li><strong>Tool</strong> — took forever to arrive on streaming (2019, finally)</li>
        <li><strong>Regional licensing</strong> — albums available in US but not Europe, and vice versa</li>
      </ul>
      <p>
        With physical media, this is never a problem. Your CD doesn't care about licensing disputes. Your vinyl doesn't 
        disappear when an artist has beef with a corporation. <strong>It's yours, period.</strong>
      </p>
    </section>

    <section>
      <h2>The Secret Weapon: Ripping Your Collection</h2>
      <p>
        "But I like the convenience of streaming!" We hear you. Here's the best-kept secret of the physical media world: 
        <strong>you can have both</strong>.
      </p>
      <p>
        Ripping your CDs (and even vinyl via USB turntables) to FLAC or MP3 is completely legal for personal use. 
        Once digitized, you can stream your <em>own</em> music library using self-hosted solutions like:
      </p>
      
      <h3>Jellyfin</h3>
      <p>
        Free, open-source media server. Works like your own personal Netflix/Spotify. Stream your ripped music 
        to any device — phone, smart TV, web browser. No subscription fees, ever.
      </p>
      
      <h3>Navidrome</h3>
      <p>
        Lightweight music server specifically designed for music collections. Compatible with Subsonic apps 
        (tons of great mobile apps available). Self-host on a Raspberry Pi for $50 one-time and stream forever.
      </p>
      
      <h3>Plex</h3>
      <p>
        More polished interface, also free for basic music streaming. Has native apps for almost every platform.
      </p>

      <p>
        The workflow is simple:
      </p>
      <ol>
        <li>Buy a CD from <a href="https://www.cdandlp.com/?affilie=columbo&utm_source=gooffline.me/blog&utm_medium=link&utm_campaign=affiliation" target="_blank" rel="noopener noreferrer sponsored">CD&LP</a> or your local shop</li>
        <li>Rip it to FLAC using free software (Exact Audio Copy on Windows, XLD on Mac)</li>
        <li>Add to your Jellyfin/Navidrome library</li>
        <li>Stream anywhere — home, car, work</li>
        <li>Keep the physical copy on your shelf (it looks cool)</li>
      </ol>
      <p>
        <strong>Result:</strong> Streaming convenience + actual ownership + no monthly fees + no disappearing music. 
        That's the dream.
      </p>
    </section>

    <section>
      <h2>Sound Quality: No Contest</h2>
      <p>
        Let's talk audio quality. Streaming services compress your music to save bandwidth. Even "lossless" 
        streaming tiers have their quirks.
      </p>
      <table className="blog-table">
        <thead>
          <tr>
            <th>Format</th>
            <th>Bitrate</th>
            <th>Quality</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Spotify Free</td>
            <td>128 kbps</td>
            <td>Low</td>
          </tr>
          <tr>
            <td>Spotify Premium</td>
            <td>320 kbps</td>
            <td>Good</td>
          </tr>
          <tr>
            <td>Apple Music Lossless</td>
            <td>~1,411 kbps</td>
            <td>Great</td>
          </tr>
          <tr>
            <td>CD (16-bit/44.1kHz)</td>
            <td>1,411 kbps</td>
            <td>Excellent</td>
          </tr>
          <tr>
            <td>Vinyl (analog)</td>
            <td>N/A (analog)</td>
            <td>Warm, rich, unique</td>
          </tr>
        </tbody>
      </table>
      <p>
        CDs give you the master quality — what the artist approved. Vinyl adds analog warmth that many 
        audiophiles prefer, especially for rock, jazz, and anything recorded before digital took over.
      </p>
    </section>

    <section>
      <h2>The Collector Value: Physical Media Appreciates</h2>
      <p>
        Unlike your streaming subscription (which has $0 resale value), physical media can be worth money. 
        Sometimes a lot of money.
      </p>
      <p>Examples of vinyl appreciation:</p>
      <ul>
        <li><strong>Original UK pressing of "The Dark Side of the Moon"</strong> — can fetch $500+</li>
        <li><strong>First pressing of Nirvana's "Nevermind"</strong> — $200-400</li>
        <li><strong>Limited color variants</strong> — regularly 2-5x original price within years</li>
        <li><strong>Japanese CDs with bonus tracks</strong> — collector's items, often $50-100</li>
      </ul>
      <p>
        Even if you don't care about resale, there's something satisfying about building a collection 
        with tangible value. Try selling your Spotify history in 30 years.
      </p>
    </section>

    <section>
      <h2>The Verdict: Mix and Match</h2>
      <p>
        We're not saying cancel your streaming subscription today (though you could). The smartest approach 
        for 2025 and beyond:
      </p>
      <ul>
        <li><strong>Buy physical copies of albums you truly love</strong> — the ones you'll listen to for life</li>
        <li><strong>Use streaming for discovery</strong> — find new music, then buy what sticks</li>
        <li><strong>Rip your CDs</strong> — get the convenience without the rental trap</li>
        <li><strong>Start a vinyl collection</strong> — it's a hobby, an aesthetic, and an investment</li>
        <li><strong>Think about legacy</strong> — what will you pass down to the next generation?</li>
      </ul>
      <p>
        The music industry wants you subscribed forever, paying monthly until you die. 
        <strong>Ownership is rebellion.</strong> Build a library that's actually yours — one that your 
        grandchildren can hold in their hands someday.
      </p>
    </section>

    <section>
      <h2>Where to Start Your Collection</h2>
      <p>
        Ready to make the switch? Check out <a href="https://www.cdandlp.com/?affilie=columbo&utm_source=gooffline.me/blog&utm_medium=link&utm_campaign=affiliation" target="_blank" rel="noopener noreferrer sponsored">CD&LP</a> — 
        one of the best marketplaces for vinyl and CDs with millions of listings from sellers worldwide. 
        Great prices, wide selection, and they've been around since 2002.
      </p>
      <p>
        Or use <a href="/">{SITE_CONFIG.name}</a> to convert your existing Spotify playlists into shopping lists. 
        Paste a playlist, see what's available on vinyl and CD, and start owning your music.
      </p>
    </section>
  </BlogLayout>
);
